import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { StorageUtilityLib, USER_SESSION_TOKEN, UserSession } from '@thehubz/client/core';
import { catchError, retry, switchMap, throwError } from 'rxjs';
import { AnonymousAuthService } from './anonymous-auth.service';

@Injectable()
export class AnonymousAuthInterceptor implements HttpInterceptor {
  constructor(
    @Inject(USER_SESSION_TOKEN) private userSessionToken: string,
    private storageUtility: StorageUtilityLib,
    private authService: AnonymousAuthService,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler) {
    // Get the auth token from the local storage.
    const userSession = this.storageUtility.getLocalStorage<UserSession>(this.userSessionToken);
    if (!this.noAuthUrl(req.url) && userSession?.access_token) {
      // Clone the request and replace the original headers with cloned headers, updated with the authorization.
      const authToken = 'Bearer ' + userSession.access_token;
      const authReq = req.clone({ setHeaders: { Authorization: authToken } });
      return this.handleRequest(authReq, next);
    }
    return this.handleRequest(req, next);
  }

  private noAuthUrl(url: string) {
    // replace hardcoded urls with some config. For now we won't add Bearer for AWS
    return url.includes('amazonaws.com');
  }

  private isLoginRequest(url: string): boolean {
    return url.includes('/auth/login');
  }

  private handleRequest(request: HttpRequest<unknown>, next: HttpHandler) {
    const request$ = next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !this.isLoginRequest(request.url)) {
          return this.authService.loginAnonymously().pipe(
            switchMap((session: UserSession) => {
              const authToken = 'Bearer ' + session.access_token;
              const authReq = request.clone({ setHeaders: { Authorization: authToken } });
              return next.handle(authReq);
            }),
          );
        } else if (error.status === 405) {
          this.authService.logout();
        }
        return throwError(() => error);
      }),
    );
    if (this.isLoginRequest(request.url)) {
      return request$;
    }

    return request$.pipe(retry(2));
  }
}
